import React, { useState, useEffect, useRef } from "react";
import { ActivityItem, DefaultButton, IActivityItemProps, PrimaryButton } from "office-ui-fabric-react";
import { commentsApi } from "../../api/commentApi";
import { Comment } from "../../models";
import { mergeStyleSets, mergeStyles } from "@fluentui/react";
import { MessageBar, MessageBarType } from "@fluentui/react";
import { Persona, PersonaSize } from "office-ui-fabric-react";
import { pushNotification } from "../../api/pushNotification";
import { TextField, IconButton, Image } from "@fluentui/react";
import ApiSpinner from "../common/ApiSpinner";
import clsx from "clsx";
import Moment from "react-moment";
import SofIcon from "../common/Icons";
import moment from "moment";

const panel = mergeStyles({
  display: "flex",
});
const panelCol = mergeStyles([
  panel,
  {
    flexDirection: "column",
  },
]);
const panelRow = mergeStyles([
  panel,
  {
    flexDirection: "row",
  },
]);
const buttonBase = mergeStyles({
  padding: "9px 10px",
  fontSize: 16,
  fontWeight: 500,
  height: 42,
  border: "none",
  color: "#4c4c4c",
  selectors: {
    [":active"]: {
      backgroundColor: "#008cc0",
    },
  },
});
const classes = mergeStyleSets({
  outerContainer: [
    panelCol,
    {
      flex: "1 1 auto",
      height: "100%",
      // height: "calc(100% - 115px)",
    },
  ],
  headerPanel: [
    panel,
    {
      alignItems: "center",
      border: "1px solid var(--grey-200)",
      borderBottom: 0,
      height: 40,
      padding: "8px 16px",
      background: "#EEF0F8",
    },
  ],
  headerTitle: {
    flexGrow: 1,
    color: "var(--secondary-700)",
    fontWeight: "700",
    fontSize: "14px",
    lineHeight: "22px",
  },
  contentPanel: {
    flex: "1 1 auto",
    border: "1px solid var(--grey-200)",
    overflowY: "auto",
    height: 0,
    padding: "10px",
  },
  footerPanel: [
    panelRow,
    {
      border: "1px solid var(--grey-200)",
      borderRadius: 2,
      marginTop: 16,
      padding: 3,
    },
  ],
  inlineBlockDiv: {
    display: "inline-block",
    paddingRight: "10px",
    width: "40px",
    marginTop: 12
  },
  bottomMarginDiv: {
    marginBottom: "4px",
  },
  emptyBigDiv: {
    color: "var(--grey-800)",
    fontSize: "16px",
    fontWeight: "bold",
    margin: "16px 0",
  },
  emptyMedDiv: {
    color: "var(--grey-400)",
    fontSize: "14px",
    maxWidth: "90%",
    margin: "auto",
  },
  roundBgDiv: {
    width: "74px",
    height: "74px",
    borderRadius: "35px",
    backgroundColor: "var(--grey-050)",
    display: "block",
    padding: "24px",
  },
  backButtonSpan: {
    paddingLeft: "5px",
    paddingRight: "5px",
  },
  zeroCommentDiv: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    textAlign: "center",
    flexDirection: "column",
    marginTop: "48px",
  },
  iconStyles: {
    color: "#353B3B",
    height: 32,
    width: 32,
  },
  commentTextDiv: {
    whiteSpace: "pre-wrap",
  },
  header: {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 24,
    color: "#00447E",
    textAlign: "left",
  },
  body: {
    textAlign: "left",
    margin: "18px 0 38px 0",
  },
  footer: {
    textAlign: "right",
  },
  button: [buttonBase, {}],
  primaryButton: [
    buttonBase,
    {
      backgroundColor: "#009FDF",
      color: "#FFFFFF",
      padding: "9px 20px",
    },
  ],
  modalButton: [
    buttonBase,
    {
      padding: "9px 20px",
    },
  ],
});

interface IProps {
  lineHandlingNo: string;
  nominationNo: string | undefined;
  isAddCommentAllowed: boolean;
  isDeleteCommentAllowed: boolean;
  isOffline: boolean;
  readonly: boolean;
  onClose(): void;
}

const getActionTimeString = (date: Date): string => date.toISOString();

const useAsync = function <T>(
  asyncFn: () => Promise<T>,
  success: (result: T) => void
): void {
  useEffect(() => {
    let isMounted = true;
    asyncFn().then((data) => {
      if (isMounted) success(data);
    });
    return () => {
      isMounted = false;
    };
  }, [asyncFn, success]);
};

const RemarksPane: React.FC<IProps> = (props: IProps) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState<string>("");
  const [commentId, setCommentId] = useState<number>(0);
  const commentEnd = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const getComments = async () => {
    const comments = await commentsApi.getCommentsAsync(
      props.lineHandlingNo,
      props.nominationNo
    );
    setComments(comments);
  };

  const onSubmit = async () => {
    try {
      await commentsApi.addCommentAsync({
        commentText,
        lineHandlingNo: props.lineHandlingNo,
        nominationNo: props.nominationNo,
        submittedOn: getActionTimeString(new Date()),
      } as Comment);
      await getComments();
      setCommentText("");
      setErrorMessage("");
    } catch {
      setErrorMessage("Unable to save changes. Please try to reconnect.");
    }
  };

  const deleteCommentClick = (commentId: number) => {
    setShowModal(true);
    setCommentId(commentId);
  };

  const deleteComment = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      await commentsApi.deleteCommentAsync(props.lineHandlingNo, commentId);
      await getComments();
      setErrorMessage("");
      setShowModal(false);
      setIsLoading(false);
    } catch (err) {
      setShowModal(false);
      setIsLoading(false);
      setErrorMessage("Unable to save changes. Please try to reconnect.");
    }
  };

  const commentActivities = comments.map(
    (c, index: number) =>
      ({
        key: index,
        activityDescription: [
          <table key={index}>
            <tr>
              <td rowSpan={2} style={{ verticalAlign: "top" }}>
                <span className={classes.inlineBlockDiv}>
                  {c.profilePicture && (
                    <Image
                      style={{
                        borderRadius: "50%",
                        height: "auto",
                      }}
                      src={`data:image/jpeg;base64, ${c.profilePicture}`}
                      width="30"
                      height="30"
                    />
                  )}
                  {!c.profilePicture && (
                    <Persona
                      size={PersonaSize.size28}
                      initialsColor={"var(--primary-500)"}
                      color={"var(--primary-500)"}
                      imageInitials={c.submittedByName
                        .split(" (")[0]
                        .split(" ")
                        .map((n, i, a) =>
                          i === 0 || i + 1 === a.length ? n[0] : null
                        )
                        .join("")}
                    />
                  )}
                </span>
              </td>
              <td>
                <b key={`submittedby-${index}`}>{c.submittedByName}</b>&nbsp;
                <Moment format="HH:mm - ll">
                  {moment.utc(c.submittedOn).local().toDate()}
                </Moment>
              </td>
              {props.isDeleteCommentAllowed && !props.readonly && (
                <IconButton
                  iconProps={{
                    iconName: "Delete",
                    className: classes.iconStyles,
                  }}
                  onClick={() => deleteCommentClick(c.commentId)}
                />
              )}
            </tr>

            <tr>
              <td>
                <div className={classes.commentTextDiv}>{c.commentText}</div>
              </td>
            </tr>
          </table>,
        ],
      } as IActivityItemProps | { key: number })
  );

  useEffect(() => {
    let isMounted = false;

    if (props.isOffline) {
      //setErrorMessage("You are offline. Comments cannot be processed.");
    } else {
      getComments();
      setErrorMessage("");
    }

    return () => {
      isMounted = false;
    };
  }, [props.isOffline]);

  useEffect(() => {
    setTimeout(() => {
      if (commentEnd.current)
        commentEnd.current.scrollIntoView({
          behavior: "smooth",
        });
    }, 100);
  }, [comments]);

  useEffect(() => {
    pushNotification.connection.on("CommentAdded", (message) => {
      var comment = message as Comment;

      if (
        comment.lineHandlingNo === props.lineHandlingNo &&
        comment.nominationNo === props.nominationNo
      ) {
        getComments();
      }
    });
  }, []);

  return (
    <>
      <div className={classes.outerContainer}>
        <div className={classes.headerPanel}>
          <span className={classes.headerTitle}>
            {props.nominationNo === "0"
              ? "Line Handling Comments"
              : "Nomination Comments"}
          </span>
        </div>
        <div className={clsx(classes.contentPanel, "custom-scroll")}>
          {isLoading && <ApiSpinner />}
          {!isLoading && commentActivities.length === 0 && (
            <div className={classes.zeroCommentDiv}>
              <div className={classes.roundBgDiv}>
                <SofIcon iconname="NoCommentV2" />
              </div>
              <div className={classes.emptyBigDiv}>No Comments Yet</div>
              <div className={classes.emptyMedDiv}>
                Use the text area below to add comments associated to&nbsp;
                {props.nominationNo === "0"
                  ? `Line Handling No ${props.lineHandlingNo.substr(
                      props.lineHandlingNo.length - 6
                    )}`
                  : `Nomination No ${
                      props.nominationNo &&
                      props.nominationNo.substr(props.nominationNo.length - 6)
                    }`}
              </div>
            </div>
          )}
          {!isLoading &&
            commentActivities.map((a, index: number) => (
              <ActivityItem
                key={`comment-${index}`}
                {...a}
                className={classes.bottomMarginDiv}
              />
            ))}
          <div id="comments-bottom" ref={commentEnd}></div>
        </div>
        {props.isAddCommentAllowed && (
          <div className={classes.footerPanel}>
            <TextField
              id="txt-comment"
              placeholder="Add comment"
              rows={1}
              multiline
              maxLength={500}
              value={commentText}
              disabled={props.readonly || isLoading}
              resizable={false}
              styles={{
                field: { lineHeight: "42px" },
                fieldGroup: { border: "none", minHeight: "auto" },
                root: { flexGrow: 1 },
              }}
              onChange={(e, newValue?: string | undefined) =>
                setCommentText(newValue === undefined ? "" : newValue)
              }
            />
            <IconButton
              id="btn-send-comment"
              iconProps={{ iconName: "Send", className: classes.iconStyles }}
              disabled={
                props.readonly || commentText.trim() === "" || isLoading
              }
              onClick={onSubmit}
              styles={{
                root: { height: 42, width: 42, margin: "6px 8px" },
              }}
            />
          </div>
        )}
        {errorMessage && (
          <MessageBar messageBarType={MessageBarType.error}>
            {errorMessage}
          </MessageBar>
        )}
      </div>

      {showModal && (
        <div className="modal-bg">
          <div className="modal-right" style={{ width: 400 }}>
            <form>
              <div className={classes.header}>Delete Comment</div>
              <div className={classes.body}>
                <span>
                  You are about to delete this comment. Do you want to proceed?
                </span>
              </div>
                          <div className={classes.footer}>
                              <PrimaryButton
                                  id="btn-submit-undo"
                                  type="submit" 
                                  onClick={deleteComment}
                                  disabled={isLoading}
                                  text="Proceed"
                              />
                {/*<button*/}
                {/*  id="btn-submit-undo"*/}
                {/*  className={classes.primaryButton}*/}
                {/*  type="submit"*/}
                {/*  onClick={deleteComment}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Proceed*/}
                {/*</button>*/}
                              &nbsp;

                              <DefaultButton
                                  id="btn-cancel-undo"
                                  onClick={() => setShowModal(false)}
                                  disabled={isLoading}
                                  text="Cancel"
                              />
                {/*<button*/}
                {/*  id="btn-cancel-undo"*/}
                {/*  className={classes.modalButton}*/}
                {/*  type="button"*/}
                {/*  onClick={() => setShowModal(false)}*/}
                {/*  disabled={isLoading}*/}
                {/*>*/}
                {/*  Cancel*/}
                {/*</button>*/}
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

export default RemarksPane;
